import React, { useState } from 'react'
import styled from 'styled-components'
import { ResponsiveText } from 'components/ResponsiveComponents'
import { Text, Button, Input, Flex, Box } from 'pancakeswap-uikit'
import { useTranslation } from 'contexts/Localization'
import { useUserSlippageTolerance, useUserTransactionTTL } from 'state/user/hooks'
import QuestionHelper from '../../QuestionHelper'

enum SlippageError {
  InvalidInput = 'InvalidInput',
  RiskyLow = 'RiskyLow',
  RiskyHigh = 'RiskyHigh',
}

enum DeadlineError {
  InvalidInput = 'InvalidInput',
}

const StyledButton = styled(Button)`
  border-radius: 100px;
  font-size: 12px;
  line-height: 100%;
  color: #1b1b1b;
  width: 52px;
  height: 32px;

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 14px;
    width: 71px;
  }
`

const StyledInput = styled(Input)<{ width; padding? }>`
  width: ${({ width }) => width};
  height: 32px;
  background: #1b1b1b;
  border-radius: 100px;
  padding: ${({ padding }) => padding || '0 16px'};
  font-family: Bebas Neue;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: #ffffff;
  border: none;
  float: right;

  &::placeholder {
    color: #ffffff;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 14px;
  }
`

const StyledSpan = styled.span`
  font-family: Bebas Neue;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  float: right;
  margin-right: 20px;
  margin-top: -23px;
  position: relative;
  z-index: 2;
  color: white;
`

const SlippageTabs = () => {
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippageTolerance()
  const [ttl, setTtl] = useUserTransactionTTL()
  const [slippageInput, setSlippageInput] = useState('')
  const [deadlineInput, setDeadlineInput] = useState('')

  const { t } = useTranslation()

  const slippageInputIsValid =
    slippageInput === '' || (userSlippageTolerance / 100).toFixed(2) === Number.parseFloat(slippageInput).toFixed(2)
  const deadlineInputIsValid = deadlineInput === '' || (ttl / 60).toString() === deadlineInput

  let slippageError: SlippageError | undefined
  if (slippageInput !== '' && !slippageInputIsValid) {
    slippageError = SlippageError.InvalidInput
  } else if (slippageInputIsValid && userSlippageTolerance < 50) {
    slippageError = SlippageError.RiskyLow
  } else if (slippageInputIsValid && userSlippageTolerance > 500) {
    slippageError = SlippageError.RiskyHigh
  } else {
    slippageError = undefined
  }

  let deadlineError: DeadlineError | undefined
  if (deadlineInput !== '' && !deadlineInputIsValid) {
    deadlineError = DeadlineError.InvalidInput
  } else {
    deadlineError = undefined
  }

  const parseCustomSlippage = (value: string) => {
    setSlippageInput(value)

    try {
      const valueAsIntFromRoundedFloat = Number.parseInt((Number.parseFloat(value) * 100).toString())
      if (!Number.isNaN(valueAsIntFromRoundedFloat) && valueAsIntFromRoundedFloat < 5000) {
        setUserSlippageTolerance(valueAsIntFromRoundedFloat)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const parseCustomDeadline = (value: string) => {
    setDeadlineInput(value)

    try {
      const valueAsInt: number = Number.parseInt(value) * 60
      if (!Number.isNaN(valueAsInt) && valueAsInt > 0) {
        setTtl(valueAsInt)
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" mb={['24px', null, '50px']}>
        <Flex mb="16px">
          <ResponsiveText
            fontFamily="Lexend"
            fontWeight="200"
            desktopFontSize="14px"
            mobileFontSize="12px"
            lineHeight="17px"
            color="#999999"
          >
            {t('Slippage Tolerance')}
          </ResponsiveText>

          <QuestionHelper
            text={t(
              'Setting a high slippage tolerance can help transactions succeed, but you may not get such a good price. Use with caution.',
            )}
            placement="top-start"
            ml="4px"
          />
        </Flex>
        <Flex flexWrap="wrap">
          <StyledButton
            mr="8px"
            scale="sm"
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(10)
            }}
            variant={userSlippageTolerance === 10 ? 'yellow' : 'tertiary'}
          >
            0.1%
          </StyledButton>
          <StyledButton
            mr="8px"
            scale="sm"
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(50)
            }}
            variant={userSlippageTolerance === 50 ? 'yellow' : 'tertiary'}
          >
            0.5%
          </StyledButton>
          <StyledButton
            mr="8px"
            scale="sm"
            onClick={() => {
              setSlippageInput('')
              setUserSlippageTolerance(100)
            }}
            variant={userSlippageTolerance === 100 ? 'yellow' : 'tertiary'}
          >
            1.0%
          </StyledButton>
          <Flex alignItems="center">
            <Box width="77px">
              <StyledInput
                width="77px"
                scale="sm"
                placeholder={(userSlippageTolerance / 100).toFixed(2)}
                value={slippageInput}
                onBlur={() => {
                  parseCustomSlippage((userSlippageTolerance / 100).toFixed(2))
                }}
                onChange={(e) => parseCustomSlippage(e.target.value)}
                isWarning={!slippageInputIsValid}
                isSuccess={![10, 50, 100].includes(userSlippageTolerance)}
              />
              <StyledSpan>%</StyledSpan>
            </Box>
          </Flex>
        </Flex>
        {!!slippageError && (
          <ResponsiveText
            fontFamily="Lexend"
            fontWeight="400"
            desktopFontSize="14px"
            mobileFontSize="12px"
            lineHeight="100%"
            color={slippageError === SlippageError.InvalidInput ? 'red' : '#F3841E'}
            mt="15px"
          >
            {slippageError === SlippageError.InvalidInput
              ? t('Enter a valid slippage percentage')
              : slippageError === SlippageError.RiskyLow
              ? t('Your transaction may fail')
              : t('')}
          </ResponsiveText>
        )}
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" mb={['16px', null, '24px']} mr={['32px', null, null]}>
        <Flex alignItems="center">
          <ResponsiveText
            fontFamily="Lexend"
            fontWeight="200"
            desktopFontSize="14px"
            mobileFontSize="12px"
            lineHeight="100%"
            color="#999999"
          >
            {t('Tx deadline (mins)')}
          </ResponsiveText>
          <QuestionHelper
            text={t('Your transaction will revert if it is left confirming for longer than this time.')}
            placement="top-start"
            ml="4px"
          />
        </Flex>
        <Flex>
          <Box width="60px">
            <StyledInput
              width="60px"
              padding="0 0 0 25px"
              scale="sm"
              color={deadlineError ? 'red' : undefined}
              onBlur={() => {
                parseCustomDeadline((ttl / 60).toString())
              }}
              placeholder={(ttl / 60).toString()}
              value={deadlineInput}
              onChange={(e) => parseCustomDeadline(e.target.value)}
            />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default SlippageTabs
